import { CREDIT_ACTION_TYPES } from './types';
import creditService from '../services/creditService';

/**
 * Fetch credit list action (Redux thunk)
 * @returns {Function} Redux thunk action
 */
export const fetchCreditListAction = () => async (dispatch) => {
  dispatch({ type: CREDIT_ACTION_TYPES.FETCH_CREDIT_LIST_REQUEST });

  try {
    const response = await creditService.getPurchaseCreditsList();

    if (response.success && response.data) {
      const creditList = Array.isArray(response.data) ? response.data : response.data?.data || response.data?.credits || [];
      dispatch({
        type: CREDIT_ACTION_TYPES.FETCH_CREDIT_LIST_SUCCESS,
        payload: creditList,
      });
      return { success: true, data: creditList };
    } else {
      const errorMessage = response.message || 'Failed to fetch credits list';
      dispatch({
        type: CREDIT_ACTION_TYPES.FETCH_CREDIT_LIST_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error?.message || 'Failed to fetch credits list';
    dispatch({
      type: CREDIT_ACTION_TYPES.FETCH_CREDIT_LIST_FAILURE,
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};


/**
 * Fetch wallet balance action (Redux thunk)
 * @returns {Function} Redux thunk action
 */
export const fetchWalletBalanceAction = (subscriberId) => async (dispatch) => {
  dispatch({ type: CREDIT_ACTION_TYPES.FETCH_WALLET_BALANCE_REQUEST });

  try {
    const response = await creditService.getWalletBalance(subscriberId);

    if (response.success && response.data != null) {
      const balance = response.data?.balance ?? response.data?.data?.balance ?? response.data;
      dispatch({
        type: CREDIT_ACTION_TYPES.FETCH_WALLET_BALANCE_SUCCESS,
        payload: balance,
      });
      return { success: true, data: balance };
    } else {
      const errorMessage = response.message || 'Failed to fetch wallet balance';
      dispatch({
        type: CREDIT_ACTION_TYPES.FETCH_WALLET_BALANCE_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error?.message || 'Failed to fetch wallet balance';
    dispatch({
      type: CREDIT_ACTION_TYPES.FETCH_WALLET_BALANCE_FAILURE,
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

/**
 * Clear credit errors action
 */
export const clearCreditErrors = () => (dispatch) => {
  dispatch({ type: CREDIT_ACTION_TYPES.CLEAR_CREDIT_ERRORS });
};

/**
 * Initiate credit purchase (CCBill) – same pattern as purchaseContent
 * @param {string} creditPackId - Credit pack ID
 * @param {Object} purchasePayload - { amount, baseAmount, extraCharge, returnUrl, vatAmount, vatPercentage, paymentTokenId? }
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const purchaseCredits = async (creditPackId, purchasePayload) => {
  try {
    if (!creditPackId) {
      return { success: false, error: 'Credit package ID is required' };
    }
    if (!purchasePayload || purchasePayload.amount == null) {
      return { success: false, error: 'Purchase amount is required' };
    }
    const response = await creditService.purchaseCredits(creditPackId, purchasePayload);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message || 'Failed to initiate credit purchase' };
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred';
    return { success: false, error: errorMessage };
  }
};

/**
 * Cancel credit purchase (pending transaction)
 * @param {string} transactionId - Transaction ID to cancel
 * @param {boolean} isManualRemove - User cancelled (true) or closed (false)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const cancelCreditPurchase = async (transactionId, isManualRemove = false) => {
  try {
    if (!transactionId) {
      return { success: false, error: 'Transaction ID is required' };
    }
    const response = await creditService.cancelCreditPurchase(transactionId, isManualRemove);
    return response.success ? { success: true, data: response.data } : { success: false, error: response.message };
  } catch (error) {
    return { success: false, error: error?.response?.data?.message || error?.message };
  }
};

/**
 * Deduct call credits after a completed call (subscriber). Refreshes wallet on success.
 * @param {{ creatorId: string, credit: string, callInMinutes: number, callInSec: number, callMediaType: string }} payload
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const deductCallCreditAction = (payload) => async (dispatch) => {
  try {
    if (!payload?.creatorId) {
      return { success: false, error: 'Creator ID is required' };
    }
    const response = await creditService.deductCallCredit(payload);
    if (response.success) {
      await dispatch(fetchWalletBalanceAction());
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message || 'Failed to deduct call credits' };
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to deduct call credits';
    return { success: false, error: errorMessage };
  }
};
